'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';

const comparisonData = [
  { factor: "Monthly Income Potential", holiday: "AED 50,000 - AED 2,00,000+", longterm: "AED 15,000 - AED 50,000", holidayBetter: true },
  { factor: "Flexibility", holiday: "Use property when needed", longterm: "Fixed 12-month commitment", holidayBetter: true },
  { factor: "Property Maintenance", holiday: "Professional cleaning & maintenance", longterm: "Tenant responsible", holidayBetter: true },
  { factor: "Market Risk", holiday: "Diversified across seasons", longterm: "Single tenant dependency", holidayBetter: true },
  { factor: "Property Appreciation", holiday: "Higher due to tourism appeal", longterm: "Standard market rate", holidayBetter: true },
  { factor: "Management Complexity", holiday: "Handled by professionals", longterm: "Landlord manages directly", holidayBetter: true },
  { factor: "Initial Setup Cost", holiday: "Higher (furnishing, amenities)", longterm: "Lower (basic setup)", holidayBetter: false },
  { factor: "Income Stability", holiday: "Seasonal variations", longterm: "Consistent monthly income", holidayBetter: false }
];

const ComparisonSection = () => {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Holiday Home vs Long-term Rental
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Discover why holiday home rentals often provide better returns and flexibility compared to traditional long-term rentals
          </p>
        </motion.div>

        {/* Comparison Images */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-8 mb-16"
        >
          <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden shadow-lg">
            <Image
              src="https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg"
              alt="Luxury holiday home rental"
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-6 left-6 text-white">
              <h3 className="text-xl md:text-2xl font-bold mb-2">Holiday Home Rental</h3>
              <p className="text-sm md:text-base opacity-90">Premium experience, higher income potential</p>
            </div>
          </div>
          <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden shadow-lg">
            <Image
              src="https://images.pexels.com/photos/1571468/pexels-photo-1571468.jpeg"
              alt="Traditional long-term rental"
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-6 left-6 text-white">
              <h3 className="text-xl md:text-2xl font-bold mb-2">Long-term Rental</h3>
              <p className="text-sm md:text-base opacity-90">Stable income, limited flexibility</p>
            </div>
          </div>
        </motion.div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="overflow-hidden rounded-2xl shadow-xl border border-gray-100"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-sm">
                <tr>
                  <th className="px-6 py-5 text-left font-semibold text-gray-900 bg-gray-50 whitespace-nowrap">Comparison Factor</th>
                  <th className={`px-6 py-5 text-center font-bold bg-primary text-secondary flex items-center justify-center gap-2 whitespace-nowrap`}>
                    Holiday Home Rental
                  </th>
                  <th className={`px-6 py-5 text-center font-semibold bg-gray-50 text-gray-900 whitespace-nowrap`}>Long-term Rental</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {comparisonData.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {row.factor}
                    </td>
                    <td className={`px-6 py-4 text-sm text-center align-middle whitespace-nowrap
                  ${row.holidayBetter ? 'font-semibold' : 'text-gray-600'}
                  ${row.holidayBetter ? 'text-secondary' : ''} `}>
                      <div className="flex items-center justify-center gap-2">
                        {row.holidayBetter ?
                          <span className={`text-secondary text-lg font-bold`}>✔️</span>
                          :
                          <span className="text-gray-400 text-lg">—</span>
                        }
                        <span className={row.holidayBetter ? `text-lg font-semibold` : 'text-gray-800'}>
                          {row.holiday}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-center align-middle text-gray-600 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-gray-400 text-lg">—</span>
                        {row.longterm}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Key Benefits Cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <div className="bg-primary/5 p-6 rounded-xl border border-primary/20 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-16 h-16 opacity-20">
              <Image
                src="https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg"
                alt="Higher income"
                fill
                className="object-cover rounded-full"
              />
            </div>
            <div className="text-3xl mb-3">💰</div>
            <h3 className="text-lg font-semibold text-primary mb-2">
              3-5x Higher Income
            </h3>
            <p className="text-gray-600 text-sm">
              Holiday rentals typically generate 3-5 times more income than long-term rentals
            </p>
          </div>

          <div className="bg-secondary/5 p-6 rounded-xl border border-secondary/20 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-16 h-16 opacity-20">
              <Image
                src="https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg"
                alt="Personal use"
                fill
                className="object-cover rounded-full"
              />
            </div>
            <div className="text-3xl mb-3">🏖️</div>
            <h3 className="text-lg font-semibold text-secondary mb-2">
              Personal Use
            </h3>
            <p className="text-gray-600 text-sm">
              Use your property for personal vacations and family getaways
            </p>
          </div>

          <div className="bg-primary/5 p-6 rounded-xl border border-primary/20 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-16 h-16 opacity-20">
              <Image
                src="https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg"
                alt="Professional management"
                fill
                className="object-cover rounded-full"
              />
            </div>
            <div className="text-3xl mb-3">🛡️</div>
            <h3 className="text-lg font-semibold text-primary mb-2">
              Professional Management
            </h3>
            <p className="text-gray-600 text-sm">
              We handle everything - bookings, cleaning, maintenance, and guest communication
            </p>
          </div>

          <div className="bg-secondary/5 p-6 rounded-xl border border-secondary/20 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-16 h-16 opacity-20">
              <Image
                src="https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg"
                alt="Market growth"
                fill
                className="object-cover rounded-full"
              />
            </div>
            <div className="text-3xl mb-3">📈</div>
            <h3 className="text-lg font-semibold text-secondary mb-2">
              Market Growth
            </h3>
            <p className="text-gray-600 text-sm">
              Tourism industry growing 15% annually, increasing demand for holiday homes
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ComparisonSection;
